import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminAPI } from '../../api/client'

// ─── Admin UI for the multi-exam framework registry (FR-029) ──────────────────
// CRUD over /admin/exams: framework metadata, enable toggle, score mapping, and
// per-skill parts. Backed entirely by the existing adminAPI exam methods.

interface Framework {
  examType: string
  displayName: string
  scoreScale: string
  scoreMappingJson: string | null
  isEnabled: boolean
  sortOrder: number
}

interface Part {
  examType: string
  skill: string
  partCode: string
  sortOrder: number
  labelVi: string | null
  labelEn: string | null
  questionCount: number | null
  timeSeconds: number | null
  taskType: string | null
  allowedQuestionTypes: string | null
}

const SKILLS = ['listening', 'reading', 'writing', 'speaking']

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

export default function ExamFrameworksTab() {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState<string | null>(null)
  const [editing, setEditing] = useState<Framework | null>(null)

  const { data: frameworks, isLoading } = useQuery({
    queryKey: ['admin-exams'],
    queryFn: adminAPI.listExams as () => Promise<Framework[]>,
  })

  const toggle = useMutation({
    mutationFn: ({ examType, isEnabled }: { examType: string; isEnabled: boolean }) =>
      adminAPI.toggleExam(examType, isEnabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-exams'] }),
  })

  if (isLoading) return <p style={{ color: '#535b71' }}>Đang tải…</p>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <p style={{ color: '#535b71', fontSize: '0.875rem', margin: 0 }}>
        Quản lý các kỳ thi (VSTEP / IELTS): bật/tắt, thang điểm, ánh xạ CEFR→điểm, và cấu trúc phần thi từng kỹ năng.
      </p>

      {(frameworks ?? []).map((fw) => (
        <div key={fw.examType} style={{ background: 'white', borderRadius: '1rem', border: '1px solid #e2e6f0', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.25rem 1.5rem' }}>
            <div style={{ flex: 1 }}>
              <H style={{ fontSize: '1.0625rem', fontWeight: 800 }}>{fw.displayName}</H>
              <div style={{ fontSize: '0.75rem', color: '#a5adc6', marginTop: '0.25rem' }}>
                <code>{fw.examType}</code> · thang điểm <strong>{fw.scoreScale}</strong> · thứ tự {fw.sortOrder}
              </div>
            </div>
            <button
              onClick={() => toggle.mutate({ examType: fw.examType, isEnabled: !fw.isEnabled })}
              disabled={toggle.isPending}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem',
                background: fw.isEnabled ? '#d1e7dd' : '#e2e3e5', color: fw.isEnabled ? '#0a3622' : '#41464b',
              }}
            >
              {fw.isEnabled ? '● Đang bật' : '○ Đang tắt'}
            </button>
            <button onClick={() => setEditing(fw)} style={btnStyle('#004be2')}>Sửa</button>
            <button onClick={() => setExpanded(expanded === fw.examType ? null : fw.examType)} style={btnStyle('#8e00d5')}>
              {expanded === fw.examType ? 'Ẩn phần thi' : 'Phần thi'}
            </button>
          </div>
          {expanded === fw.examType && <PartsPanel examType={fw.examType} />}
        </div>
      ))}

      {editing && <FrameworkEditModal framework={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}

// ─── Parts management ─────────────────────────────────────────────────────────

function PartsPanel({ examType }: { examType: string }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)

  const { data: parts, isLoading } = useQuery({
    queryKey: ['admin-exam-parts', examType],
    queryFn: () => adminAPI.getExamParts(examType) as Promise<Part[]>,
  })

  const del = useMutation({
    mutationFn: (p: Part) => adminAPI.deleteExamPart(examType, p.skill, p.partCode),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-exam-parts', examType] }),
  })

  return (
    <div style={{ borderTop: '1px solid #e2e6f0', padding: '1.25rem 1.5rem', background: '#f8f9fd' }}>
      {isLoading ? (
        <p style={{ color: '#535b71', fontSize: '0.8125rem' }}>Đang tải phần thi…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#a5adc6' }}>
              <th style={thStyle}>Kỹ năng</th><th style={thStyle}>Mã phần</th><th style={thStyle}>Nhãn (VI)</th>
              <th style={thStyle}>Số câu</th><th style={thStyle}>Thời gian (s)</th><th style={thStyle}>Task</th><th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {(parts ?? []).map((p) => (
              <tr key={`${p.skill}-${p.partCode}`} style={{ borderTop: '1px solid #eef0f6' }}>
                <td style={tdStyle}>{p.skill}</td>
                <td style={tdStyle}><code>{p.partCode}</code></td>
                <td style={tdStyle}>{p.labelVi ?? '—'}</td>
                <td style={tdStyle}>{p.questionCount ?? '—'}</td>
                <td style={tdStyle}>{p.timeSeconds ?? '—'}</td>
                <td style={tdStyle}>{p.taskType ?? '—'}</td>
                <td style={tdStyle}>
                  <button onClick={() => del.mutate(p)} disabled={del.isPending} style={btnStyle('#b41340')}>Xoá</button>
                </td>
              </tr>
            ))}
            {(parts ?? []).length === 0 && (
              <tr><td colSpan={7} style={{ ...tdStyle, color: '#a5adc6' }}>Chưa có phần thi nào.</td></tr>
            )}
          </tbody>
        </table>
      )}
      <button onClick={() => setAdding(true)} style={{ ...btnStyle('#004be2'), marginTop: '0.75rem' }}>+ Thêm phần thi</button>
      {adding && <PartEditModal examType={examType} onClose={() => setAdding(false)} />}
    </div>
  )
}

// ─── Modals ───────────────────────────────────────────────────────────────────

function FrameworkEditModal({ framework, onClose }: { framework: Framework; onClose: () => void }) {
  const qc = useQueryClient()
  const [displayName, setDisplayName] = useState(framework.displayName)
  const [scoreScale, setScoreScale] = useState(framework.scoreScale)
  const [sortOrder, setSortOrder] = useState(String(framework.sortOrder))
  const [mapping, setMapping] = useState(framework.scoreMappingJson ?? '{}')
  const [err, setErr] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      let scoreMapping: Record<string, string> | undefined
      if (mapping.trim()) {
        scoreMapping = JSON.parse(mapping) // throws → caught below
      }
      await adminAPI.upsertExam(framework.examType, { displayName, scoreScale, sortOrder: Number(sortOrder), isEnabled: framework.isEnabled, scoreMapping })
      if (scoreMapping) await adminAPI.updateExamScoreMapping(framework.examType, scoreMapping)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-exams'] }); onClose() },
    onError: (e: any) => setErr(e?.message?.includes('JSON') ? 'Ánh xạ điểm phải là JSON hợp lệ' : (e?.message ?? 'Lỗi')),
  })

  return (
    <Modal title={`Sửa kỳ thi: ${framework.examType}`} onClose={onClose}>
      <Field label="Tên hiển thị"><input style={inputStyle} value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></Field>
      <Field label="Thang điểm (vd: 0-10, 0-9)"><input style={inputStyle} value={scoreScale} onChange={(e) => setScoreScale(e.target.value)} /></Field>
      <Field label="Thứ tự sắp xếp"><input style={inputStyle} type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} /></Field>
      <Field label="Ánh xạ CEFR→điểm (JSON)"><textarea style={{ ...inputStyle, minHeight: '7rem', fontFamily: 'monospace' }} value={mapping} onChange={(e) => setMapping(e.target.value)} /></Field>
      {err && <p style={{ color: '#b41340', fontSize: '0.8125rem' }}>{err}</p>}
      <ModalActions onClose={onClose} onSave={() => { setErr(null); save.mutate() }} saving={save.isPending} />
    </Modal>
  )
}

function PartEditModal({ examType, onClose }: { examType: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    skill: 'listening', partCode: '', sortOrder: '0', labelVi: '', labelEn: '',
    questionCount: '', timeSeconds: '', taskType: '', allowedQuestionTypes: '',
  })
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const save = useMutation({
    mutationFn: () => adminAPI.upsertExamPart(examType, {
      skill: form.skill,
      partCode: form.partCode.trim(),
      sortOrder: Number(form.sortOrder) || 0,
      labelVi: form.labelVi || null,
      labelEn: form.labelEn || null,
      questionCount: form.questionCount ? Number(form.questionCount) : null,
      timeSeconds: form.timeSeconds ? Number(form.timeSeconds) : null,
      taskType: form.taskType || null,
      allowedQuestionTypes: form.allowedQuestionTypes
        ? form.allowedQuestionTypes.split(',').map((s) => s.trim()).filter(Boolean)
        : null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-exam-parts', examType] }); onClose() },
  })

  return (
    <Modal title="Thêm / sửa phần thi" onClose={onClose}>
      <Field label="Kỹ năng">
        <select style={inputStyle} value={form.skill} onChange={(e) => set('skill', e.target.value)}>
          {SKILLS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <Field label="Mã phần (vd: part1, passage1, task1)"><input style={inputStyle} value={form.partCode} onChange={(e) => set('partCode', e.target.value)} /></Field>
      <Field label="Nhãn (VI)"><input style={inputStyle} value={form.labelVi} onChange={(e) => set('labelVi', e.target.value)} /></Field>
      <Field label="Nhãn (EN)"><input style={inputStyle} value={form.labelEn} onChange={(e) => set('labelEn', e.target.value)} /></Field>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <Field label="Số câu"><input style={inputStyle} type="number" value={form.questionCount} onChange={(e) => set('questionCount', e.target.value)} /></Field>
        <Field label="Thời gian (s)"><input style={inputStyle} type="number" value={form.timeSeconds} onChange={(e) => set('timeSeconds', e.target.value)} /></Field>
        <Field label="Thứ tự"><input style={inputStyle} type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} /></Field>
      </div>
      <Field label="Task type (tuỳ chọn)"><input style={inputStyle} value={form.taskType} onChange={(e) => set('taskType', e.target.value)} /></Field>
      <Field label="Loại câu hỏi cho phép (phẩy ngăn cách)"><input style={inputStyle} value={form.allowedQuestionTypes} onChange={(e) => set('allowedQuestionTypes', e.target.value)} /></Field>
      <ModalActions onClose={onClose} onSave={() => save.mutate()} saving={save.isPending} disabled={!form.partCode.trim()} />
    </Modal>
  )
}

// ─── Small UI helpers ─────────────────────────────────────────────────────────

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(6,14,32,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '1rem', padding: '1.75rem', width: '32rem', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 800, marginBottom: '1.25rem' }}>{title}</H>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: '0.875rem', flex: 1 }}>
      <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#535b71', marginBottom: '0.375rem' }}>{label}</span>
      {children}
    </label>
  )
}

function ModalActions({ onClose, onSave, saving, disabled }: { onClose: () => void; onSave: () => void; saving: boolean; disabled?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
      <button onClick={onClose} style={{ ...btnStyle('#535b71'), background: '#eef0ff' }}>Huỷ</button>
      <button onClick={onSave} disabled={saving || disabled} style={{ ...btnStyle('white'), background: disabled ? '#a5adc6' : '#004be2' }}>
        {saving ? 'Đang lưu…' : 'Lưu'}
      </button>
    </div>
  )
}

const btnStyle = (color: string): React.CSSProperties => ({
  padding: '0.4rem 0.9rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer',
  fontWeight: 700, fontSize: '0.75rem', background: color === 'white' ? '#004be2' : 'rgba(0,0,0,0.04)', color,
})
const thStyle: React.CSSProperties = { padding: '0.5rem 0.625rem', fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em' }
const tdStyle: React.CSSProperties = { padding: '0.5rem 0.625rem', color: '#272e42' }
const inputStyle: React.CSSProperties = { width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', border: '1px solid #d1dcff', outline: 'none', fontSize: '0.875rem', boxSizing: 'border-box' }
